import { SelectText } from '@amelio/ui-library';
import i18next from 'i18next';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUpdateSurveyLanguage } from '@/api/engagement-user';
import amelioLogo from '@/assets/logo/amelio-logo.png';
import { companyAtom } from '@/atoms/company';
import { reviewAnswerAtom } from '@/atoms/reviewAnswer';
import { userInstanceAtom } from '@/atoms/userDetails';
import { SessionStorageKey } from '@/constants/StorageKeys';
import i18n from '@/plugins/i18n';
import { getLanguageNativeName } from '@/utils/localization';

export const SurveyBanner = () => {
    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
    const company = useAtomValue(companyAtom);
    const user = useAtomValue(userInstanceAtom);
    const reviewAnswer = useAtomValue(reviewAnswerAtom);
    const { mutate: updateSurveyLanguage } = useUpdateSurveyLanguage();

    const surveyLanguages = useMemo(
        () =>
            reviewAnswer?.surveyLanguages ??
            user?.surveyLanguages ??
            company?.company?.surveyLanguages ??
            [],
        [reviewAnswer, company, user],
    );

    const selectedLanguageLabel = useMemo(
        () => getLanguageNativeName(selectedLanguage),
        [selectedLanguage],
    );

    const languageOptions = useMemo(
        () =>
            surveyLanguages
                .filter((lang) => lang !== selectedLanguage)
                .map((lang) => ({
                    label: getLanguageNativeName(lang),
                    value: lang,
                }))
                // Ordered by the label on screen, so the list reads alphabetically to whoever is
                // looking at it. `localeCompare` places accented names where a reader expects
                // them, e.g. "Čeština" with the C entries rather than after Z (#11595).
                .sort((a, b) => a.label.localeCompare(b.label)),
        [surveyLanguages, selectedLanguage],
    );

    const handleLanguageChange = useCallback(
        (lang: string) => {
            // Preserve the user's explicit choice across reloads — the BE-stored value may briefly lag behind.
            sessionStorage.setItem(SessionStorageKey.SURVEY_LANGUAGE_OVERRIDE, lang);
            // The answer's identifier is the reviewer's only credential: the server reads from it whose
            // language changes, so no user id leaves the page.
            const reviewAnswerId = reviewAnswer?.id;
            if (reviewAnswerId) {
                updateSurveyLanguage({ reviewAnswerId, lang });
            }
            i18next.changeLanguage(lang);
            setSelectedLanguage(lang);
        },
        [reviewAnswer?.id, updateSurveyLanguage],
    );

    useEffect(() => {
        const handleI18nLanguageChanged = () => {
            setSelectedLanguage(i18n.language);
        };

        i18n.on('languageChanged', handleI18nLanguageChanged);

        return () => {
            i18n.off('languageChanged', handleI18nLanguageChanged);
        };
    }, []);

    return (
        <header
            className={
                'fixed top-0 right-0 left-0 z-100 flex w-full items-center justify-between border-b border-gray-200 bg-white/50 px-8 py-4 shadow-sm backdrop-blur-2xl'
            }
        >
            <div className={'ml-2 flex items-center'}>
                {/* The survey is answered outside the shell: the logo leaves the module for good,
                    with a full load, so it is a plain link and never a routed one. */}
                <a
                    href={'/'}
                    data-testid={'survey-banner-logo'}
                >
                    <img
                        src={amelioLogo}
                        alt={'Amelio-logo'}
                        className={'h-8 cursor-pointer'}
                    />
                </a>
            </div>

            {languageOptions.length > 0 && (
                <div className={'mr-2'}>
                    <SelectText
                        options={languageOptions}
                        placeholder={selectedLanguageLabel}
                        onChange={handleLanguageChange}
                        dataTestId={'survey-language-selector'}
                        placement={'bottom-right'}
                    />
                </div>
            )}
        </header>
    );
};
